/**
 * Every user-facing string in the dashboard, in both languages.
 *
 * Nothing renders text that is not in here — no label, placeholder, aria-label,
 * toast, empty state or page title. Adding an English value without its Arabic
 * twin does not compile.
 */

import { errorStrings } from './error-strings';
import type { Pair } from './pair';

export const strings = {
  app: {
    name: { ar: 'DIÓRA', en: 'DIÓRA' }, // artwork, never translated
    tagline: { ar: 'مطعم ومقهى', en: 'Resto & Cafe' },
    dashboard: { ar: 'لوحة التحكم', en: 'Dashboard' },
    skipToContent: { ar: 'تخطَّ إلى المحتوى', en: 'Skip to content' },
    mainNav: { ar: 'التنقل الرئيسي', en: 'Main navigation' },
    openMenu: { ar: 'افتح القائمة', en: 'Open menu' },
    closeMenu: { ar: 'أغلق القائمة', en: 'Close menu' },
    loading: { ar: 'جارٍ التحميل…', en: 'Loading…' },
    signedInAs: { ar: 'مسجَّل الدخول باسم', en: 'Signed in as' },
    signOut: { ar: 'تسجيل الخروج', en: 'Sign out' },
    account: { ar: 'الحساب', en: 'Account' },
    crashed: { ar: 'تعذّر عرض هذه الصفحة.', en: 'This page could not be shown.' },
    crashedHint: {
      ar: 'بقية اللوحة تعمل. أعد المحاولة، أو أعد تحميل الصفحة.',
      en: 'The rest of the dashboard still works. Try again, or reload the page.',
    },
  },

  locale: {
    label: { ar: 'لغة الواجهة', en: 'Interface language' },
    ar: { ar: 'العربية', en: 'العربية' },
    en: { ar: 'English', en: 'English' },
  },

  theme: {
    label: { ar: 'المظهر', en: 'Theme' },
    light: { ar: 'فاتح', en: 'Light' },
    dark: { ar: 'داكن', en: 'Dark' },
    system: { ar: 'حسب النظام', en: 'System' },
  },

  nav: {
    overview: { ar: 'نظرة عامة', en: 'Overview' },
    dashboard: { ar: 'الرئيسية', en: 'Home' },
    readiness: { ar: 'جاهزية الإطلاق', en: 'Launch readiness' },
    menuGroup: { ar: 'القائمة', en: 'Menu' },
    categories: { ar: 'الفئات', en: 'Categories' },
    items: { ar: 'الأصناف', en: 'Items' },
    addons: { ar: 'الإضافات', en: 'Add-ons' },
    offers: { ar: 'العروض', en: 'Offers' },
    placeGroup: { ar: 'المكان', en: 'Place' },
    branches: { ar: 'الفروع', en: 'Branches' },
    contentGroup: { ar: 'المحتوى', en: 'Content' },
    home: { ar: 'الصفحة الرئيسية', en: 'Home page' },
    about: { ar: 'من نحن', en: 'About' },
    faqs: { ar: 'الأسئلة الشائعة', en: 'FAQs' },
    media: { ar: 'الصور', en: 'Media' },
    systemGroup: { ar: 'النظام', en: 'System' },
    settings: { ar: 'الإعدادات', en: 'Settings' },
    users: { ar: 'المستخدمون', en: 'Users' },
    qr: { ar: 'رموز QR', en: 'QR codes' },
    audit: { ar: 'سجل التغييرات', en: 'Activity log' },
  },

  auth: {
    signInTitle: { ar: 'تسجيل الدخول', en: 'Sign in' },
    signInSubtitle: {
      ar: 'لوحة تحكم فريق ديورا.',
      en: 'The Dióra staff dashboard.',
    },
    email: { ar: 'البريد الإلكتروني', en: 'Email' },
    emailPlaceholder: { ar: 'name@dioraresto.com', en: 'name@dioraresto.com' },
    password: { ar: 'كلمة المرور', en: 'Password' },
    showPassword: { ar: 'أظهر كلمة المرور', en: 'Show password' },
    hidePassword: { ar: 'أخفِ كلمة المرور', en: 'Hide password' },
    submit: { ar: 'دخول', en: 'Sign in' },
    submitting: { ar: 'جارٍ الدخول…', en: 'Signing in…' },
    changePasswordTitle: { ar: 'غيّر كلمة المرور', en: 'Change your password' },
    changePasswordForced: {
      ar: 'كلمة مرورك الحالية مؤقتة وأُنشئت لك. اختر واحدة جديدة للمتابعة.',
      en: 'Your current password was generated for you. Choose a new one to continue.',
    },
    currentPassword: { ar: 'كلمة المرور الحالية', en: 'Current password' },
    newPassword: { ar: 'كلمة المرور الجديدة', en: 'New password' },
    confirmPassword: { ar: 'تأكيد كلمة المرور', en: 'Confirm password' },
    passwordRule: { ar: '١٢ محرفاً على الأقل.', en: 'At least 12 characters.' },
    passwordMismatch: { ar: 'الكلمتان غير متطابقتين.', en: 'These two do not match.' },
    changeSubmit: { ar: 'حفظ كلمة المرور', en: 'Save password' },
    changed: { ar: 'تم تغيير كلمة المرور.', en: 'Password changed.' },
    // Changing the password revokes every session for the account, this one
    // included, so the screen has to say why the person is back at the form.
    changedSignInAgain: {
      ar: 'تم تغيير كلمة المرور. سجّل الدخول بكلمة المرور الجديدة.',
      en: 'Password changed. Sign in with your new password.',
    },
    sessionEnded: { ar: 'انتهت الجلسة.', en: 'Session ended.' },
  },

  common: {
    save: { ar: 'حفظ', en: 'Save' },
    saving: { ar: 'جارٍ الحفظ…', en: 'Saving…' },
    saved: { ar: 'تم الحفظ.', en: 'Saved.' },
    // Accessible name for the drawn riyal mark — see .dio-riyal.
    currencyName: { ar: 'ريال سعودي', en: 'Saudi riyal' },
    cancel: { ar: 'إلغاء', en: 'Cancel' },
    close: { ar: 'إغلاق', en: 'Close' },
    create: { ar: 'إنشاء', en: 'Create' },
    created: { ar: 'تم الإنشاء.', en: 'Created.' },
    edit: { ar: 'تعديل', en: 'Edit' },
    // The row action menu's trigger. It is an icon button, so this is the only
    // name it has — for a screen reader and for anyone driving by voice.
    moreActions: { ar: 'إجراءات أخرى', en: 'More actions' },
    remove: { ar: 'إزالة', en: 'Remove' },
    add: { ar: 'إضافة', en: 'Add' },
    confirm: { ar: 'تأكيد', en: 'Confirm' },
    search: { ar: 'بحث', en: 'Search' },
    searchPlaceholder: { ar: 'ابحث بالعربية أو الإنجليزية…', en: 'Search either language…' },
    filter: { ar: 'تصفية', en: 'Filter' },
    all: { ar: 'الكل', en: 'All' },
    none: { ar: 'لا شيء', en: 'None' },
    notSet: { ar: 'غير محدَّد', en: 'Not set' },
    optional: { ar: 'اختياري', en: 'Optional' },
    required: { ar: 'مطلوب', en: 'Required' },
    yes: { ar: 'نعم', en: 'Yes' },
    no: { ar: 'لا', en: 'No' },
    back: { ar: 'رجوع', en: 'Back' },
    reload: { ar: 'إعادة التحميل', en: 'Reload' },
    retry: { ar: 'أعد المحاولة', en: 'Try again' },
    loading: { ar: 'جارٍ التحميل…', en: 'Loading…' },
    noResults: { ar: 'لا نتائج.', en: 'Nothing here.' },
    noResultsHint: { ar: 'جرّب كلمة بحث أخرى أو غيّر التصفية.', en: 'Try another search, or change the filter.' },
    somethingBroke: { ar: 'تعذّر تحميل هذا.', en: 'That did not load.' },
    page: { ar: 'صفحة', en: 'Page' },
    previous: { ar: 'السابق', en: 'Previous' },
    next: { ar: 'التالي', en: 'Next' },
    showingCount: { ar: '{{from}}–{{to}} من {{total}}', en: '{{from}}–{{to}} of {{total}}' },
    moveUp: { ar: 'حرّك لأعلى', en: 'Move up' },
    moveDown: { ar: 'حرّك لأسفل', en: 'Move down' },
    reorder: { ar: 'إعادة الترتيب', en: 'Reorder' },
    reordered: { ar: 'تم حفظ الترتيب.', en: 'Order saved.' },
    lastUpdated: { ar: 'آخر تحديث', en: 'Last updated' },
    version: { ar: 'النسخة', en: 'Version' },
    actions: { ar: 'إجراءات', en: 'Actions' },
    // Column headings for the list tables. Visually hidden where the column
    // carries a thumbnail or a button — a column still needs a name, because
    // that name is what a screen reader announces before every cell in it.
    status: { ar: 'الحالة', en: 'Status' },
    image: { ar: 'صورة', en: 'Image' },
    openMenuFor: { ar: 'إجراءات {{name}}', en: 'Actions for {{name}}' },
    unsavedChanges: { ar: 'لديك تغييرات غير محفوظة.', en: 'You have unsaved changes.' },
    discardBody: {
      ar: 'إذا خرجت الآن ستُفقد هذه التغييرات. لا يمكن التراجع عن ذلك.',
      en: 'Leaving now loses them. That cannot be undone.',
    },
    discard: { ar: 'تجاهل التغييرات', en: 'Discard changes' },
    noSuchRecord: {
      ar: 'لم نعثر على هذا السجل. ربما حُذف أو أن الرابط غير صحيح.',
      en: 'That record could not be found. It may have been archived, or the address is wrong.',
    },
    copy: { ar: 'نسخ', en: 'Copy' },
    copied: { ar: 'نُسخ.', en: 'Copied.' },
    download: { ar: 'تنزيل', en: 'Download' },
    select: { ar: 'اختر…', en: 'Choose…' },
    clear: { ar: 'مسح', en: 'Clear' },
    preview: { ar: 'معاينة', en: 'Preview' },
    details: { ar: 'التفاصيل', en: 'Details' },
  },

  /**
   * Why a field blocks publication, keyed on the API's code.
   *
   * The field name alone is lossy — `longitude` can be absent or out of range,
   * `timezone` absent or invalid — and `categoryId` never means "no category
   * was chosen", only that the chosen one is unpublished. The dashboard used to
   * infer meaning from the name and got that last one exactly backwards.
   *
   * REQUIRED_FOR_PUBLICATION and TRANSLATION_REQUIRED are deliberately absent:
   * the field's own label already says it, and "Name (Arabic) — requires a
   * translation" is noise.
   */
  publishReason: {
    CATEGORY_NOT_PUBLISHED: {
      ar: 'الفئة التي ينتمي إليها هذا الصنف غير منشورة بعد',
      en: 'the category this item belongs to is not published yet',
    },
    AT_LEAST_ONE_ITEM_REQUIRED: {
      ar: 'أضف صنفاً واحداً على الأقل',
      en: 'add at least one item',
    },
    END_BEFORE_START: {
      ar: 'تاريخ الانتهاء يسبق تاريخ البدء',
      en: 'the end date comes before the start date',
    },
    OUT_OF_RANGE: {
      ar: 'القيمة خارج النطاق المسموح',
      en: 'the value is outside the allowed range',
    },
    TIMEZONE_NOT_CONFIGURED: {
      ar: 'لم تُضبط المنطقة الزمنية للفرع',
      en: 'the branch has no time zone set',
    },
    INVALID_TIMEZONE: {
      ar: 'المنطقة الزمنية غير معروفة',
      en: 'that time zone is not recognised',
    },
    IMAGE_TOO_SMALL: {
      ar: 'الصورة أصغر من المقاس الموصى به، وقد كُبِّرت لتملأ التصميم',
      en: 'the photo is smaller than recommended and was enlarged to fill the layout',
    },
    NOT_BELOW_REGULAR_PRICE: {
      ar: 'سعر العرض ليس أقل من السعر العادي',
      en: 'the offer price is not below the regular price',
    },
    REGULAR_PRICE_IS_ZERO: {
      ar: 'السعر العادي صفر، فلا يمكن حساب خصم منه',
      en: 'the regular price is zero, so no discount can be taken from it',
    },
    NON_NEGATIVE_DECIMAL_REQUIRED: {
      ar: 'يجب أن يكون رقماً غير سالب',
      en: 'must be a number that is not negative',
    },
    PRICE_OUT_OF_RANGE: {
      ar: 'السعر خارج النطاق المسموح',
      en: 'the price is outside the allowed range',
    },
  },

  bilingual: {
    arabic: { ar: 'العربية', en: 'Arabic' },
    english: { ar: 'الإنجليزية', en: 'English' },
    missing: { ar: 'ناقص', en: 'Missing' },
    bothRequired: {
      ar: 'اللغتان مطلوبتان قبل النشر.',
      en: 'Both languages are needed before this can be published.',
    },
  },

  status: {
    label: { ar: 'الحالة', en: 'Status' },
    DRAFT: { ar: 'مسوّدة', en: 'Draft' },
    PUBLISHED: { ar: 'منشور', en: 'Published' },
    ARCHIVED: { ar: 'مؤرشف', en: 'Archived' },
    AVAILABLE: { ar: 'متوفّر', en: 'Available' },
    UNAVAILABLE: { ar: 'غير متوفّر', en: 'Unavailable' },
    upcoming: { ar: 'قادم', en: 'Upcoming' },
    active: { ar: 'فعّال', en: 'Running' },
    expired: { ar: 'منتهٍ', en: 'Ended' },
    openNow: { ar: 'مفتوح الآن', en: 'Open now' },
    closedNow: { ar: 'مغلق الآن', en: 'Closed now' },
    hoursUnknown: { ar: 'الأوقات غير محدَّدة', en: 'Hours not set' },
  },

  publish: {
    publish: { ar: 'نشر', en: 'Publish' },
    published: { ar: 'تم النشر.', en: 'Published.' },
    unpublish: { ar: 'إلغاء النشر', en: 'Unpublish' },
    unpublished: { ar: 'أُلغي النشر.', en: 'Unpublished.' },
    archive: { ar: 'أرشفة', en: 'Archive' },
    archived: { ar: 'تمت الأرشفة.', en: 'Archived.' },
    restore: { ar: 'استعادة', en: 'Restore' },
    restored: { ar: 'استُعيد كمسوّدة.', en: 'Restored as a draft.' },
    restoreNote: {
      ar: 'الاستعادة تُرجع السجل إلى مسوّدة، لا إلى منشور.',
      en: 'Restoring returns a record to draft, not straight to live.',
    },
    blockedTitle: { ar: 'لا يمكن النشر بعد', en: 'Not ready to publish' },
    blockedIntro: { ar: 'أصلح ما يلي قبل النشر:', en: 'Fix the following before publishing:' },
    // The last-resort name for a blocked field the UI has no label for. It is
    // deliberately a real phrase: the fallback used to print the API's own
    // field name, which put a bare Latin `categoryId` inside an Arabic alert.
    blockedField: { ar: 'حقل مطلوب', en: 'A required field' },
    // The advisory alert. It leads with "this will publish" on purpose: the
    // same sentence used to be a refusal, and a warning that reads like one
    // teaches people to stop at it.
    noteTitle: { ar: 'يُنشر كما هو، مع ملاحظة', en: 'This will publish, with a note' },
    noteIntro: {
      ar: 'لا شيء هنا يمنع النشر. يمكنك استبدال الصورة لاحقاً.',
      en: 'None of this blocks publishing. You can replace the photo later.',
    },
    confirmUnpublishTitle: { ar: 'إلغاء النشر؟', en: 'Unpublish this?' },
    confirmUnpublishBody: {
      ar: 'سيختفي من الموقع فوراً. يمكنك نشره مرة أخرى في أي وقت.',
      en: 'It disappears from the site immediately. You can publish it again at any time.',
    },
    confirmArchiveTitle: { ar: 'أرشفة؟', en: 'Archive this?' },
    confirmArchiveBody: {
      ar: 'تُخفيه الأرشفة من كل مكان. لا يُحذف شيء، ويمكن استعادته لاحقاً.',
      en: 'Archiving hides it everywhere. Nothing is deleted, and it can be restored later.',
    },
  },

  dashboard: {
    title: { ar: 'نظرة عامة', en: 'Overview' },
    greeting: { ar: 'أهلاً، {{name}}', en: 'Hello, {{name}}' },
    categories: { ar: 'الفئات', en: 'Categories' },
    items: { ar: 'الأصناف', en: 'Items' },
    publishedOf: { ar: '{{published}} منشور من {{total}}', en: '{{published}} of {{total}} published' },
    unavailableItems: { ar: 'أصناف غير متوفّرة', en: 'Items marked unavailable' },
    addonGroups: { ar: 'مجموعات الإضافات', en: 'Add-on groups' },
    branches: { ar: 'الفروع', en: 'Branches' },
    faqs: { ar: 'الأسئلة الشائعة', en: 'FAQs' },
    mediaTotal: { ar: 'الصور', en: 'Images' },
    offersActive: { ar: 'عروض فعّالة', en: 'Offers running' },
    offersUpcoming: { ar: 'عروض قادمة', en: 'Offers upcoming' },
    offersExpired: { ar: 'عروض منتهية', en: 'Offers ended' },
    gapsTitle: { ar: 'نواقص الترجمة', en: 'Translation gaps' },
    gapsEmpty: {
      ar: 'لا نواقص. كل سجل يحمل اللغتين.',
      en: 'No gaps. Every record carries both languages.',
    },
    gapsIntro: {
      ar: 'سجلات تنقصها إحدى اللغتين. المنشور منها يظهر للضيوف ناقصاً.',
      en: 'Records missing a language. The published ones reach guests that way.',
    },
    gapsCount: { ar: '{{count}} سجلاً', en: '{{count}} records' },
    readinessTitle: { ar: 'جاهزية الإطلاق', en: 'Launch readiness' },
    readinessReady: { ar: 'كل ما يمنع الإطلاق مكتمل.', en: 'Nothing blocking is outstanding.' },
    readinessBlocked: { ar: '{{count}} مانع للإطلاق.', en: '{{count}} blocking the launch.' },
    readinessWarning: { ar: 'تحذير', en: 'Warning' },
    readinessBlocking: { ar: 'مانع', en: 'Blocking' },
    readinessDone: { ar: 'مكتمل', en: 'Done' },
  },

  readiness: {
    currency: { ar: 'تحديد العملة', en: 'Currency set' },
    defaultTimezone: { ar: 'المنطقة الزمنية الافتراضية', en: 'Default time zone set' },
    contactChannel: { ar: 'وسيلة تواصل واحدة على الأقل', en: 'At least one contact channel' },
    publishedCategories: { ar: 'فئة منشورة واحدة على الأقل', en: 'At least one published category' },
    publishedItems: { ar: 'صنف منشور واحد على الأقل', en: 'At least one published item' },
    aboutPublished: { ar: 'نشر صفحة من نحن', en: 'About page published' },
    publishedBranches: { ar: 'فرع منشور واحد على الأقل', en: 'At least one published branch' },
    branchHoursComplete: { ar: 'كل فرع منشور له أوقات ومنطقة زمنية', en: 'Every published branch has hours and a zone' },
    activeOwner: { ar: 'مالك فعّال واحد على الأقل', en: 'At least one active owner' },
    mediaWritable: { ar: 'مخزن الصور قابل للكتابة', en: 'Image storage is writable' },
  },

  categories: {
    title: { ar: 'فئات القائمة', en: 'Menu categories' },
    subtitle: {
      ar: 'ترتيب الفئات هنا هو ترتيبها في القائمة المنشورة.',
      en: 'The order here is the order guests read.',
    },
    newTitle: { ar: 'فئة جديدة', en: 'New category' },
    editTitle: { ar: 'تعديل الفئة', en: 'Edit category' },
    name: { ar: 'اسم الفئة', en: 'Category name' },
    description: { ar: 'وصف الفئة', en: 'Category description' },
    image: { ar: 'صورة الفئة', en: 'Category image' },
    imageHint: {
      ar: 'يُفضَّل ٦٠٠×٦٠٠ أو أكبر. الأصغر مقبول ويُكبَّر تلقائياً.',
      en: '600×600 or larger is best. Smaller is accepted and enlarged.',
    },
    itemCount: { ar: 'الأصناف', en: 'Items' },
    empty: { ar: 'لا فئات بعد.', en: 'No categories yet.' },
    emptyHint: { ar: 'الفئة الأولى هي أول ما يراه الضيف.', en: 'The first category is the first thing a guest reads.' },
    createHint: { ar: 'الاسم العربي وحده يكفي لحفظ مسوّدة.', en: 'The Arabic name alone is enough to save a draft.' },
    archiveHasItems: {
      ar: 'تحتوي هذه الفئة على {{count}} صنفاً منشوراً. أرشفتها تُخفيها كلها.',
      en: 'This category holds {{count}} published items. Archiving hides every one of them.',
    },
    archiveConfirm: { ar: 'أرشفة الفئة وإخفاء أصنافها', en: 'Archive the category and hide its items' },
  },

  items: {
    title: { ar: 'الأصناف', en: 'Menu items' },
    subtitle: { ar: 'كل طبق، بلغتيه وسعره وصورته.', en: 'Every dish, in both languages, with its price and photo.' },
    newTitle: { ar: 'صنف جديد', en: 'New item' },
    editTitle: { ar: 'تعديل الصنف', en: 'Edit item' },
    name: { ar: 'اسم الصنف', en: 'Item name' },
    description: { ar: 'الوصف', en: 'Description' },
    category: { ar: 'الفئة', en: 'Category' },
    basePrice: { ar: 'السعر', en: 'Price' },
    priceHint: { ar: 'رقم واحد بخانتين عشريتين، بدون رمز العملة.', en: 'One number, two decimals, no currency symbol.' },
    noPrice: { ar: 'بلا سعر', en: 'No price' },
    image: { ar: 'صورة الصنف', en: 'Item photo' },
    imageHint: {
      ar: 'يُفضَّل ٨٠٠×٦٠٠ أو أكبر. الأصغر مقبول ويُكبَّر تلقائياً.',
      en: '800×600 or larger is best. Smaller is accepted and enlarged.',
    },
    availability: { ar: 'التوفّر', en: 'Availability' },
    markUnavailable: { ar: 'علِّمه غير متوفّر', en: 'Mark unavailable' },
    markAvailable: { ar: 'علِّمه متوفّراً', en: 'Mark available' },
    availabilityHint: {
      ar: 'غير المتوفّر يبقى ظاهراً في القائمة مع إشارة، ولا يختفي.',
      en: 'An unavailable dish stays on the menu with a note rather than disappearing.',
    },
    addonGroups: { ar: 'مجموعات الإضافات', en: 'Add-on groups' },
    addonGroupsHint: {
      ar: 'المجموعة غير المنشورة لا تظهر للضيف حتى لو كانت مرتبطة هنا.',
      en: 'An unpublished group stays hidden from guests even when it is linked here.',
    },
    // Named, not just "Save": it sits in the same dialog as the dialog's own
    // Save and writes something different. Two buttons that both announce as
    // "Save" are a coin toss for anyone reading them out of context.
    saveAddonGroups: { ar: 'حفظ مجموعات الإضافات', en: 'Save add-on groups' },
    empty: { ar: 'لا أصناف بعد.', en: 'No items yet.' },
    emptyHint: { ar: 'أنشئ فئة أولاً، ثم أضف إليها الأصناف.', en: 'Create a category first, then add items to it.' },
    filterByCategory: { ar: 'حسب الفئة', en: 'By category' },
    inCategory: { ar: 'في {{name}}', en: 'In {{name}}' },
  },

  addons: {
    title: { ar: 'مجموعات الإضافات', en: 'Add-on groups' },
    subtitle: {
      ar: 'مجموعة واحدة تُربط بعدة أصناف — الحجم، درجة التحميص، الإضافات.',
      en: 'One group, reused across items — size, roast, extras.',
    },
    newGroup: { ar: 'مجموعة جديدة', en: 'New group' },
    editGroup: { ar: 'تعديل المجموعة', en: 'Edit group' },
    groupName: { ar: 'اسم المجموعة', en: 'Group name' },
    groupDescription: { ar: 'وصف المجموعة', en: 'Group description' },
    options: { ar: 'الخيارات', en: 'Options' },
    optionsWithCount: { ar: 'الخيارات ({{count}})', en: 'Options ({{count}})' },
    // A group is a container — "Extra", "Size" — and the price belongs to each
    // option inside it, not to the group. Without this the group form reads as
    // one that forgot its price field, which is exactly how it was reported.
    priceLivesOnOptions: {
      ar: 'السعر يُحدَّد لكل خيار داخل هذه المجموعة، لا للمجموعة نفسها.',
      en: 'Each option in this group carries its own price; the group itself has none.',
    },
    newOption: { ar: 'خيار جديد', en: 'New option' },
    editOption: { ar: 'تعديل الخيار', en: 'Edit option' },
    optionName: { ar: 'اسم الخيار', en: 'Option name' },
    optionPrice: { ar: 'السعر الإضافي', en: 'Extra charge' },
    optionPriceHint: {
      ar: 'اتركه فارغاً إن لم يُحدَّد بعد. الصفر يعني بلا زيادة.',
      en: 'Leave it empty if it is not decided. Zero means no extra charge.',
    },
    freeOption: { ar: 'بلا زيادة', en: 'No extra charge' },
    unpricedOption: { ar: 'بلا سعر', en: 'Not priced' },
    usageCount: { ar: 'مستخدمة في', en: 'Used on' },
    usageItems: { ar: '{{count}} صنفاً', en: '{{count}} items' },
    optionCount: { ar: 'الخيارات', en: 'Options' },
    empty: { ar: 'لا مجموعات إضافات.', en: 'No add-on groups.' },
    emptyOptions: { ar: 'لا خيارات في هذه المجموعة.', en: 'This group has no options.' },
    emptyGroupPublishable: {
      ar: 'يمكن نشر مجموعة فارغة — لا تظهر للضيف حتى تُضاف إليها خيارات.',
      en: 'An empty group can be published — guests simply do not see it until it has options.',
    },
    archiveWarning: {
      ar: 'هذه المجموعة مرتبطة بـ {{count}} صنفاً. الأرشفة تُخفيها عنها جميعاً، وتبقي الروابط سليمة.',
      en: 'This group is on {{count}} items. Archiving hides it from all of them and keeps the links intact.',
    },
    unpublishNote: {
      ar: 'إلغاء النشر يُخفيها عن كل صنف دون المساس بأي ارتباط.',
      en: 'Unpublishing hides it from every item without touching a single link.',
    },
  },

  offers: {
    title: { ar: 'العروض', en: 'Offers' },
    subtitle: { ar: 'خصومات محدّدة بوقت، وإعلانات بلا أسعار.', en: 'Time-boxed discounts, and announcements with no prices.' },
    newTitle: { ar: 'عرض جديد', en: 'New offer' },
    editTitle: { ar: 'تعديل العرض', en: 'Edit offer' },
    offerTitle: { ar: 'عنوان العرض', en: 'Offer title' },
    description: { ar: 'الوصف', en: 'Description' },
    terms: { ar: 'الشروط', en: 'Terms' },
    type: { ar: 'النوع', en: 'Type' },
    ITEM_DISCOUNT: { ar: 'خصم على أصناف', en: 'Item discount' },
    ANNOUNCEMENT: { ar: 'إعلان', en: 'Announcement' },
    typeHint: {
      ar: 'الإعلان لا يحمل أسعاراً؛ تغيير النوع يزيل الأصناف المرتبطة.',
      en: 'An announcement carries no prices; changing the type removes the attached items.',
    },
    // The list's column heading for the pair of dates. The form still labels
    // them separately as startsAt / endsAt; a column has room for one name.
    window: { ar: 'المدة', en: 'Runs' },
    startsAt: { ar: 'يبدأ', en: 'Starts' },
    endsAt: { ar: 'ينتهي', en: 'Ends' },
    windowHint: {
      ar: 'البداية محتسبة والنهاية غير محتسبة، فينتهي عرض ويبدأ آخر في اللحظة نفسها بلا تداخل.',
      en: 'The start counts and the end does not, so one sale can hand over to the next at the same instant.',
    },
    image: { ar: 'صورة العرض', en: 'Offer image' },
    items: { ar: 'الأصناف المشمولة', en: 'Items in this offer' },
    addItem: { ar: 'أضف صنفاً', en: 'Add an item' },
    regularPrice: { ar: 'السعر العادي', en: 'Regular price' },
    promotionalPrice: { ar: 'سعر العرض', en: 'Offer price' },
    saving: { ar: 'توفير {{percent}}٪', en: '{{percent}}% off' },
    notVisible: { ar: 'غير ظاهر للضيوف', en: 'Not visible to guests' },
    notVisibleHint: {
      ar: 'هذا الصنف غير منشور، فلن يظهر في العرض.',
      en: 'This item is not published, so it will not appear in the offer.',
    },
    empty: { ar: 'لا عروض.', en: 'No offers.' },
    emptyItems: { ar: 'لا أصناف في هذا العرض بعد.', en: 'No items in this offer yet.' },
    conflictsTitle: { ar: 'تعارض في المواعيد', en: 'Overlapping windows' },
    conflictsIntro: {
      ar: 'هذا الصنف مشمول بعروض أخرى في المدة نفسها:',
      en: 'This item is already in another offer over the same period:',
    },
    filterState: { ar: 'حسب الحالة', en: 'By state' },
    expiredNote: { ar: 'العروض المنتهية لا تمنع عرضاً جديداً.', en: 'An ended offer never blocks a new one.' },
  },

  branches: {
    title: { ar: 'الفروع', en: 'Branches' },
    subtitle: { ar: 'لكل فرع عنوانه ومنطقته الزمنية وأوقاته.', en: 'Each with its own address, time zone and hours.' },
    newTitle: { ar: 'فرع جديد', en: 'New branch' },
    editTitle: { ar: 'تعديل الفرع', en: 'Edit branch' },
    name: { ar: 'اسم الفرع', en: 'Branch name' },
    address: { ar: 'العنوان', en: 'Address' },
    city: { ar: 'المدينة', en: 'City' },
    area: { ar: 'المنطقة', en: 'Area' },
    phone: { ar: 'الهاتف', en: 'Phone' },
    coordinates: { ar: 'الإحداثيات', en: 'Coordinates' },
    latitude: { ar: 'خط العرض', en: 'Latitude' },
    longitude: { ar: 'خط الطول', en: 'Longitude' },
    coordinatesHint: {
      ar: 'الاثنان معاً أو لا شيء — نصف إحداثي يشير إلى البحر.',
      en: 'Both together or neither — half a pair points into the sea.',
    },
    mapsUrl: { ar: 'رابط الخريطة', en: 'Map link' },
    timezone: { ar: 'المنطقة الزمنية', en: 'Time zone' },
    timezoneHint: {
      ar: 'منطقة IANA حقيقية. بدونها لا يستطيع الخادم تأكيد الفتح من الإغلاق.',
      en: 'A real IANA zone. Without one the server cannot tell a guest whether you are open.',
    },
    image: { ar: 'صورة الفرع', en: 'Branch photo' },
    imageOptional: { ar: 'الصورة اختيارية هنا.', en: 'A photo is optional for a branch.' },
    hoursNote: { ar: 'ملاحظة على الأوقات', en: 'Note about the hours' },
    closures: { ar: 'الإغلاقات', en: 'Closures' },
    empty: { ar: 'لا فروع.', en: 'No branches.' },
    openState: { ar: 'الحالة الآن', en: 'Right now' },
    hoursUnreadable: {
      ar: 'تعذّرت قراءة جدول الأوقات المخزَّن. أعد ضبطه قبل النشر.',
      en: 'The stored schedule could not be read. Set it again before publishing.',
    },
  },

  hours: {
    title: { ar: 'أوقات العمل', en: 'Opening hours' },
    subtitle: {
      ar: 'الأسبوع يُحفظ كاملاً دفعة واحدة، لأن ليلة الأحد قد تمتد إلى صباح الاثنين.',
      en: 'The week saves as a whole, because Sunday night can run into Monday morning.',
    },
    closed: { ar: 'مغلق', en: 'Closed' },
    open24: { ar: 'مفتوح ٢٤ ساعة', en: 'Open 24 hours' },
    intervals: { ar: 'فترات', en: 'Intervals' },
    opensAt: { ar: 'يفتح', en: 'Opens' },
    closesAt: { ar: 'يغلق', en: 'Closes' },
    closesNextDay: { ar: 'يغلق في اليوم التالي', en: 'Closes the next day' },
    closesNextDayNote: {
      ar: 'يغلق {{time}} من اليوم التالي',
      en: 'Closes at {{time}} the next day',
    },
    addInterval: { ar: 'أضف فترة', en: 'Add an interval' },
    removeInterval: { ar: 'احذف الفترة', en: 'Remove interval' },
    copyToAll: { ar: 'انسخ إلى بقية الأيام', en: 'Copy to every day' },
    saveWeek: { ar: 'حفظ الأسبوع', en: 'Save the week' },
    previewTitle: { ar: 'الأيام السبعة القادمة', en: 'The next seven days' },
    previewHint: {
      ar: 'بعد تطبيق الإغلاقات — هذا ما سيقرأه الضيف فعلاً.',
      en: 'Closures already applied — this is what a guest actually reads.',
    },
    notConfigured: { ar: 'لم تُضبط الأوقات بعد.', en: 'No hours set yet.' },
    notConfiguredHint: {
      ar: 'الأوقات غير المضبوطة تظهر «غير محدَّدة»، لا «مغلق».',
      en: 'Unset hours read as "not set", never as "closed".',
    },
  },

  closures: {
    title: { ar: 'الإغلاقات والاستثناءات', en: 'Closures and exceptions' },
    subtitle: {
      ar: 'الأعياد ورمضان والمناسبات الخاصة تتقدّم على الجدول الأسبوعي.',
      en: 'Holidays, Ramadan and private events override the weekly pattern.',
    },
    newTitle: { ar: 'إغلاق جديد', en: 'New closure' },
    editTitle: { ar: 'تعديل الإغلاق', en: 'Edit closure' },
    startDate: { ar: 'من تاريخ', en: 'From' },
    endDate: { ar: 'إلى تاريخ', en: 'To' },
    datesInclusive: {
      ar: 'التاريخان محتسبان — «مغلق ١–٣ كانون الثاني» يشمل الثالث.',
      en: 'Both dates count — "closed 1–3 January" includes the 3rd.',
    },
    kind: { ar: 'النوع', en: 'Kind' },
    CLOSED: { ar: 'مغلق تماماً', en: 'Closed' },
    MODIFIED_HOURS: { ar: 'أوقات معدّلة', en: 'Modified hours' },
    reason: { ar: 'السبب', en: 'Reason' },
    reasonHint: { ar: 'يظهر للضيوف، فاكتبه باللغتين.', en: 'Guests read this, so write it in both languages.' },
    empty: { ar: 'لا إغلاقات مسجَّلة.', en: 'No closures recorded.' },
    appliesNote: {
      ar: 'الإغلاق يلغي الفترات التي تبدأ في يومه — سهرة الليلة السابقة تبقى كما هي.',
      en: 'A closure removes the intervals that start on its date — the previous night stays intact.',
    },
  },

  home: {
    title: { ar: 'الصفحة الرئيسية', en: 'Home page' },
    subtitle: { ar: 'ما يظهر أولاً، وبأي ترتيب.', en: 'What shows first, and in what order.' },
    heroHeading: { ar: 'عنوان الواجهة', en: 'Hero heading' },
    heroSubheading: { ar: 'العنوان الفرعي', en: 'Hero subheading' },
    heroImage: { ar: 'صورة الواجهة', en: 'Hero image' },
    aboutTeaser: { ar: 'مقدّمة «من نحن»', en: 'About teaser' },
    sections: { ar: 'الأقسام الظاهرة', en: 'Visible sections' },
    sectionAboutPreview: { ar: 'لمحة من نحن', en: 'About preview' },
    sectionFeaturedItems: { ar: 'أصناف مميّزة', en: 'Featured items' },
    sectionActiveOffers: { ar: 'العروض الفعّالة', en: 'Running offers' },
    sectionBranchesPreview: { ar: 'لمحة الفروع', en: 'Branches preview' },
    sectionFaqPreview: { ar: 'لمحة الأسئلة الشائعة', en: 'FAQ preview' },
    sectionContactStrip: { ar: 'شريط التواصل', en: 'Contact strip' },
    featured: { ar: 'الأصناف المميّزة', en: 'Featured items' },
    featuredHint: { ar: 'ثمانية كحد أقصى.', en: 'Eight at most.' },
    featuredHidden: {
      ar: '{{count}} من المختارة غير منشورة، وستُحذف بصمت من الصفحة.',
      en: '{{count}} of these are not published and will be dropped from the page silently.',
    },
    addFeatured: { ar: 'أضف صنفاً مميّزاً', en: 'Add a featured item' },
  },

  about: {
    title: { ar: 'صفحة من نحن', en: 'About page' },
    pageTitle: { ar: 'العنوان', en: 'Title' },
    introduction: { ar: 'المقدّمة', en: 'Introduction' },
    body: { ar: 'النص', en: 'Body' },
    bodyHint: {
      ar: 'يُنظَّف النص عند الحفظ: تُقبل الروابط والعناوين والقوائم فقط.',
      en: 'The text is cleaned on save: links, headings and lists are kept, nothing else.',
    },
    image: { ar: 'الصورة', en: 'Image' },
    seoTitle: { ar: 'عنوان محركات البحث', en: 'SEO title' },
    seoDescription: { ar: 'وصف محركات البحث', en: 'SEO description' },
    charactersUsed: { ar: '{{count}} من {{max}} محرف', en: '{{count}} of {{max}} characters' },
  },

  faqs: {
    title: { ar: 'الأسئلة الشائعة', en: 'FAQs' },
    subtitle: { ar: 'ترتيبها هنا هو ترتيبها في الصفحة.', en: 'The order here is the order on the page.' },
    newTitle: { ar: 'سؤال جديد', en: 'New question' },
    editTitle: { ar: 'تعديل السؤال', en: 'Edit question' },
    question: { ar: 'السؤال', en: 'Question' },
    answer: { ar: 'الجواب', en: 'Answer' },
    empty: { ar: 'لا أسئلة بعد.', en: 'No questions yet.' },
    halfAnswer: {
      ar: 'نصف جواب أسوأ من لا شيء — اللغتان مطلوبتان للنشر.',
      en: 'Half an answer is worse than none — both languages are needed to publish.',
    },
  },

  media: {
    title: { ar: 'الصور', en: 'Media' },
    subtitle: {
      ar: 'تُعاد معالجة كل صورة إلى WebP، وتُمحى بيانات EXIF بما فيها موقع التصوير.',
      en: 'Every upload is re-encoded to WebP, which strips EXIF — including where the photo was taken.',
    },
    upload: { ar: 'رفع صورة', en: 'Upload an image' },
    uploading: { ar: 'جارٍ الرفع…', en: 'Uploading…' },
    uploaded: { ar: 'تم الرفع.', en: 'Uploaded.' },
    dropHint: { ar: 'PNG أو JPEG أو WebP أو AVIF.', en: 'PNG, JPEG, WebP or AVIF.' },
    alt: { ar: 'النص البديل', en: 'Alt text' },
    altHint: {
      ar: 'يصف الصورة لمن لا يراها. مطلوب باللغتين.',
      en: 'Describes the photo for anyone who cannot see it. Both languages.',
    },
    dimensions: { ar: 'الأبعاد', en: 'Dimensions' },
    fileSize: { ar: 'الحجم', en: 'Size' },
    uploadedBy: { ar: 'رفعها', en: 'Uploaded by' },
    usage: { ar: 'أين تُستخدم', en: 'Where it is used' },
    usageEmpty: { ar: 'غير مستخدمة بعد.', en: 'Not used anywhere yet.' },
    tooSmallFor: { ar: 'أصغر من اللازم لـ: {{surfaces}}', en: 'Too small for: {{surfaces}}' },
    forCategory: { ar: 'الفئات', en: 'categories' },
    forItem: { ar: 'الأصناف', en: 'items' },
    forOffer: { ar: 'العروض', en: 'offers' },
    empty: { ar: 'لا صور بعد.', en: 'No images yet.' },
    archiveInUse: {
      ar: 'لا يمكن أرشفة صورة يستخدمها سجل منشور — الأرشفة تترك صورة مكسورة في القائمة.',
      en: 'An image a published record points at cannot be archived — it would leave a broken photo on the menu.',
    },
    selectImage: { ar: 'اختر صورة', en: 'Choose an image' },
    // The form uploads directly; the library is for reusing an image.
    chooseExisting: { ar: 'من المكتبة', en: 'From the library' },
    changeImage: { ar: 'تغيير الصورة', en: 'Change image' },
    removeImage: { ar: 'إزالة الصورة', en: 'Remove image' },
    noImage: { ar: 'بلا صورة', en: 'No image' },
  },

  settings: {
    title: { ar: 'الإعدادات', en: 'Settings' },
    brand: { ar: 'العلامة', en: 'Brand' },
    brandName: { ar: 'اسم المطعم', en: 'Restaurant name' },
    logo: { ar: 'الشعار', en: 'Logo' },
    contact: { ar: 'التواصل', en: 'Contact' },
    phone: { ar: 'الهاتف', en: 'Phone' },
    email: { ar: 'البريد الإلكتروني', en: 'Email' },
    whatsapp: { ar: 'واتساب', en: 'WhatsApp' },
    social: { ar: 'روابط التواصل', en: 'Social links' },
    socialPlatform: { ar: 'المنصّة', en: 'Platform' },
    socialUrl: { ar: 'الرابط', en: 'Link' },
    socialEnabled: { ar: 'ظاهر', en: 'Shown' },
    addSocial: { ar: 'أضف رابطاً', en: 'Add a link' },
    socialHint: { ar: 'روابط https فقط.', en: 'https links only.' },
    seo: { ar: 'محركات البحث', en: 'Search engines' },
    seoTitle: { ar: 'العنوان', en: 'Title' },
    seoDescription: { ar: 'الوصف', en: 'Description' },
    ogImage: { ar: 'صورة المشاركة', en: 'Share image' },
    footer: { ar: 'نص التذييل', en: 'Footer text' },
    priceNote: { ar: 'ملاحظة الأسعار', en: 'Price note' },
    priceNoteHint: { ar: 'مثل: الأسعار تشمل الضريبة.', en: 'Such as: prices include tax.' },
    regional: { ar: 'العملة والتوقيت', en: 'Currency and time' },
    regionalOwnerOnly: { ar: 'للمالك فقط.', en: 'Owner only.' },
    currency: { ar: 'العملة', en: 'Currency' },
    // The code, not the currency's name — 'ريال سعودي' is refused.
    currencyHint: {
      ar: 'رمز ISO 4217 من ثلاثة أحرف، مثل SAR للريال السعودي.',
      en: 'A three-letter ISO 4217 code, such as SAR for the Saudi riyal.',
    },
    defaultTimezone: { ar: 'المنطقة الزمنية الافتراضية', en: 'Default time zone' },
    currencyChangeTitle: { ar: 'تغيير العملة', en: 'Change the currency' },
    currencyChangeBody: {
      ar: 'لا يُحوَّل أي سعر. كل رقم مخزَّن يبقى كما هو ويتغيّر معناه.',
      en: 'No price is converted. Every stored number stays as it is and changes meaning.',
    },
    currencyChangeAffects: {
      ar: 'يمسّ {{items}} صنفاً و{{addons}} خياراً و{{offers}} سعر عرض.',
      en: 'Affects {{items}} items, {{addons}} options and {{offers}} offer prices.',
    },
    currencyChangeConfirm: { ar: 'أفهم ذلك، غيّر العملة', en: 'I understand, change it' },
  },

  users: {
    title: { ar: 'المستخدمون', en: 'Users' },
    subtitle: { ar: 'من يدخل إلى اللوحة، وبأي صلاحية.', en: 'Who gets in, and with what role.' },
    newTitle: { ar: 'مستخدم جديد', en: 'New user' },
    editTitle: { ar: 'تعديل المستخدم', en: 'Edit user' },
    displayName: { ar: 'الاسم', en: 'Name' },
    email: { ar: 'البريد الإلكتروني', en: 'Email' },
    role: { ar: 'الصلاحية', en: 'Role' },
    OWNER: { ar: 'مالك', en: 'Owner' },
    EDITOR: { ar: 'محرّر', en: 'Editor' },
    VIEWER: { ar: 'مطّلع', en: 'Viewer' },
    roleOwnerHint: { ar: 'كل شيء، بما فيه المستخدمون والعملة.', en: 'Everything, including users and currency.' },
    roleEditorHint: { ar: 'تحرير المحتوى ونشره.', en: 'Edit and publish content.' },
    roleViewerHint: { ar: 'قراءة فقط.', en: 'Read only.' },
    active: { ar: 'فعّال', en: 'Active' },
    inactive: { ar: 'معطّل', en: 'Deactivated' },
    deactivate: { ar: 'تعطيل', en: 'Deactivate' },
    activate: { ar: 'تفعيل', en: 'Activate' },
    lastLogin: { ar: 'آخر دخول', en: 'Last signed in' },
    neverSignedIn: { ar: 'لم يدخل بعد', en: 'Never signed in' },
    mustChangePassword: { ar: 'عليه تغيير كلمة المرور', en: 'Must change password' },
    resetPassword: { ar: 'إعادة تعيين كلمة المرور', en: 'Reset password' },
    revokeTokens: { ar: 'إنهاء كل الجلسات', en: 'End all sessions' },
    revokeTokensDone: { ar: 'أُنهيت جلساته.', en: 'Their sessions were ended.' },
    generatedTitle: { ar: 'كلمة المرور المؤقتة', en: 'Temporary password' },
    generatedBody: {
      ar: 'تُعرض مرة واحدة فقط. انسخها وسلّمها بنفسك — لا تُرسَل بالبريد ولا يمكن استرجاعها.',
      en: 'Shown once only. Copy it and hand it over yourself — it is not emailed and cannot be retrieved.',
    },
    generatedNote: {
      ar: 'سيُطلب منه تغييرها عند أول دخول.',
      en: 'They will be asked to change it the first time they sign in.',
    },
    passwordsAreGenerated: {
      ar: 'كلمات المرور تُولَّد ولا تُختار — من يختار كلمة مرور لزميله يختارها بضعف ثم يعرفها.',
      en: 'Passwords are generated, never chosen — someone picking a colleague’s password picks it badly and then knows it.',
    },
    empty: { ar: 'لا مستخدمين.', en: 'No users.' },
  },

  qr: {
    title: { ar: 'رموز QR', en: 'QR codes' },
    subtitle: {
      ar: 'الرمز المطبوع لا يتغيّر. أرشفة الفرع تُعطّل وجهته ثم تُعيدها الاستعادة.',
      en: 'A printed code never changes. Archiving a branch turns its target off; restoring turns it back on.',
    },
    general: { ar: 'القائمة العامة', en: 'General menu' },
    branch: { ar: 'فرع', en: 'Branch' },
    target: { ar: 'الوجهة', en: 'Target' },
    openTarget: { ar: 'افتح الوجهة', en: 'Open target' },
    downloadSvg: { ar: 'تنزيل SVG', en: 'Download SVG' },
    empty: { ar: 'لا وجهات.', en: 'No targets.' },
  },

  audit: {
    title: { ar: 'سجل التغييرات', en: 'Activity log' },
    subtitle: { ar: 'من غيّر ماذا، ومتى.', en: 'Who changed what, and when.' },
    actor: { ar: 'المستخدم', en: 'Who' },
    action: { ar: 'الإجراء', en: 'Action' },
    entity: { ar: 'السجل', en: 'Record' },
    when: { ar: 'الوقت', en: 'When' },
    system: { ar: 'النظام', en: 'System' },
    viewChange: { ar: 'عرض التغيير', en: 'View the change' },
    before: { ar: 'قبل', en: 'Before' },
    after: { ar: 'بعد', en: 'After' },
    redactedNote: {
      ar: 'الحقول الحسّاسة محجوبة في المصدر.',
      en: 'Sensitive fields are redacted at the source.',
    },
    empty: { ar: 'لا تغييرات مسجّلة.', en: 'Nothing recorded yet.' },
  },

  errors: errorStrings,
} satisfies Record<string, Record<string, Pair | Record<string, Pair>>>;

export type Strings = typeof strings;
